// Screenshot capture using html2canvas
import html2canvas from "html2canvas-pro";
class Screenshot {
    constructor(hooks, captureDelay, quality, format) {
        this.captureDelay = 500; // Default delay before capture
        this.hooks = {};
        this.quality = 0.95; // Default quality for JPEG/WebP
        this.format = "png"; // Default format
        this.currentScreenshot = null;
        console.log("Screenshot module initialized", { hooks, captureDelay, quality, format });
        if (hooks)
            this.hooks = hooks;
        if (captureDelay !== undefined)
            this.captureDelay = captureDelay;
        if (quality !== undefined)
            this.quality = quality;
        if (format)
            this.format = format;
    }
    /**
     * Wait for specified milliseconds
     */
    wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    /**
     * Wait for animations to complete and DOM to stabilize
     */
    async waitForStability() {
        console.log("⏳ Waiting for page stability...");
        // Wait for initial stabilization
        await this.wait(this.captureDelay);
        // Wait for currently loading images (NO scrolling to avoid misalignment)
        // The onclone callback will handle image src copying
        await this.waitForImages();
        // Wait for fonts to load
        await this.waitForFonts();
        // Brief wait for final rendering
        await this.wait(100);
        console.log("✅ Page stabilized and ready for capture");
    }
    /**
     * Wait for visible images to finish loading (no page scrolling to avoid annotation misalignment)
     */
    async waitForImages() {
        const images = Array.from(document.images);
        // Filter to only images that are currently loading
        const loadingImages = images.filter((img) => !img.complete || img.naturalHeight === 0);
        if (loadingImages.length === 0) {
            console.log("✅ All visible images already loaded");
            return;
        }
        console.log(`⏳ Waiting for ${loadingImages.length} loading images...`);
        const imagePromises = loadingImages.map((img) => {
            return new Promise((resolve) => {
                if (img.complete && img.naturalHeight !== 0) {
                    resolve();
                    return;
                }
                const onLoad = () => {
                    console.log(`✅ Image loaded`);
                    resolve();
                };
                const onError = () => {
                    console.warn(`❌ Image failed to load`);
                    resolve(); // Still resolve to not block
                };
                const onTimeout = () => {
                    console.warn(`⏰ Image timed out after 2s`);
                    resolve();
                };
                img.addEventListener("load", onLoad, { once: true });
                img.addEventListener("error", onError, { once: true });
                // Shorter timeout - 2 seconds max
                setTimeout(onTimeout, 2000);
            });
        });
        await Promise.all(imagePromises);
        console.log(`✅ All ${loadingImages.length} images processed`);
    }
    /**
     * Wait for fonts to load
     */
    async waitForFonts() {
        if ("fonts" in document) {
            try {
                await Promise.race([
                    document.fonts.ready,
                    this.wait(1000), // Timeout after 1 second
                ]);
            }
            catch (e) {
                // Font loading failed, continue anyway
                console.warn("Font loading check failed:", e);
            }
        }
    }
    /**
     * Force a reflow to ensure styles are applied
     */
    forceReflow() {
        // Reading offsetHeight forces a reflow
        document.body.offsetHeight;
    }
    async capture(mode = "fullpage") {
        const context = {
            mode,
            timestamp: Date.now(),
        };
        try {
            // Hook: onCaptureStart - notify that capture is starting
            if (this.hooks.onCaptureStart) {
                await Promise.resolve(this.hooks.onCaptureStart(context));
            }
            // Show loading screen immediately (before hiding UI)
            this.showLoadingScreen();
            // Wait for DOM to stabilize, animations to complete, and assets to load
            await this.waitForStability();
            // Hide loading screen and UXbert UI elements BEFORE capturing
            this.hideLoadingScreen();
            this.hideUXbertElements();
            // Force a reflow to ensure all styles are computed
            this.forceReflow();
            // Hook: beforeCapture - allow developers to modify DOM before capture
            if (this.hooks.beforeCapture) {
                await Promise.resolve(this.hooks.beforeCapture(context));
            }
            let canvas;
            if (mode === "viewport") {
                // Capture only the current viewport
                canvas = await html2canvas(document.body, {
                    useCORS: true,
                    allowTaint: false,
                    logging: false,
                    width: window.innerWidth,
                    height: window.innerHeight,
                    windowWidth: window.innerWidth,
                    windowHeight: window.innerHeight,
                    x: window.scrollX,
                    y: window.scrollY,
                    // Improved rendering options
                    backgroundColor: null,
                    imageTimeout: 15000, // Increased timeout for slow-loading images
                    removeContainer: true,
                    scale: window.devicePixelRatio || 1,
                    onclone: async (clonedDoc) => {
                        await this.wait(3000);
                        // Ensure all styles are applied in the cloned document
                        const clonedBody = clonedDoc.body;
                        // Force all animations to their final state
                        const animatedElements = clonedBody.querySelectorAll("*");
                        animatedElements.forEach((el) => {
                            const element = el;
                            // Pause all animations at their end state
                            element.style.animationPlayState = "paused";
                            element.style.animationDelay = "-9999s";
                            element.style.animationIterationCount = "1";
                            // Disable transitions
                            element.style.transition = "none";
                        });
                        // Process images in the cloned document
                        const originalImages = document.body.querySelectorAll("img");
                        const clonedImages = clonedBody.querySelectorAll("img");
                        clonedImages.forEach((clonedImg, index) => {
                            const originalImg = originalImages[index];
                            if (originalImg) {
                                // Copy the current src from original to ensure it loads
                                clonedImg.src = originalImg.currentSrc || originalImg.src;
                                // Force visibility
                                clonedImg.style.opacity = "1";
                                clonedImg.style.visibility = "visible";
                                clonedImg.style.display = originalImg.style.display || "inline";
                                if (originalImg.width)
                                    clonedImg.width = originalImg.width;
                                if (originalImg.height)
                                    clonedImg.height = originalImg.height;
                                // Remove lazy loading attributes that might interfere
                                clonedImg.removeAttribute("loading");
                                clonedImg.removeAttribute("data-src");
                                clonedImg.removeAttribute("data-srcset");
                            }
                        });
                    },
                    ignoreElements: (element) => {
                        // Ignore loading screen by ID
                        if (element.id === "uxbert-screenshot-loading") {
                            return true;
                        }
                        // Ignore Uxbert elements
                        if (element.getAttribute("data-uxbert-aminah") !== null ||
                            element.className?.toString().includes("uxbert-") ||
                            element.id?.includes("uxbert-")) {
                            return true;
                        }
                        // Skip cross-origin images that might cause issues
                        if (element.tagName === "IMG") {
                            const img = element;
                            try {
                                // Test if image is accessible
                                const testCanvas = document.createElement("canvas");
                                const ctx = testCanvas.getContext("2d");
                                testCanvas.width = 1;
                                testCanvas.height = 1;
                                ctx?.drawImage(img, 0, 0, 1, 1);
                                testCanvas.toDataURL();
                                return false; // Include the image
                            }
                            catch (e) {
                                console.warn("Skipping cross-origin image:", img.src);
                                return true;
                            }
                        }
                        return false;
                    },
                });
            }
            else {
                document.querySelectorAll("video").forEach((video) => {
                    if (video.style.position === "fixed" || video.classList.contains("fixed")) {
                        video.remove();
                    }
                });
                // Capture the full page
                canvas = await html2canvas(document.body, {
                    useCORS: true,
                    allowTaint: false,
                    logging: false,
                    // Improved rendering options
                    backgroundColor: null,
                    imageTimeout: 15000, // Increased timeout for slow-loading images
                    removeContainer: true,
                    scale: window.devicePixelRatio || 1,
                    scrollY: 0,
                    scrollX: 0,
                    onclone: async (clonedDoc) => {
                        // Ensure all styles are applied in the cloned document
                        const clonedBody = clonedDoc.body;
                        // Force all animations to their final state
                        const animatedElements = clonedBody.querySelectorAll("*");
                        animatedElements.forEach((el) => {
                            const element = el;
                            // Pause all animations at their end state
                            element.style.animationPlayState = "paused";
                            element.style.animationDelay = "-9999s";
                            element.style.animationIterationCount = "1";
                            // Disable transitions
                            element.style.transition = "none";
                        });
                        // Process images in the cloned document
                        const originalImages = document.body.querySelectorAll("img");
                        const clonedImages = clonedBody.querySelectorAll("img");
                        clonedImages.forEach((clonedImg, index) => {
                            const originalImg = originalImages[index];
                            if (originalImg) {
                                // Copy the current src from original to ensure it loads
                                clonedImg.src = originalImg.currentSrc || originalImg.src;
                                // Force visibility
                                clonedImg.style.opacity = "1";
                                clonedImg.style.visibility = "visible";
                                clonedImg.style.display = originalImg.style.display || "inline";
                                if (originalImg.width)
                                    clonedImg.width = originalImg.width;
                                if (originalImg.height)
                                    clonedImg.height = originalImg.height;
                                // Remove lazy loading attributes that might interfere
                                clonedImg.removeAttribute("loading");
                                clonedImg.removeAttribute("data-src");
                                clonedImg.removeAttribute("data-srcset");
                            }
                        });
                    },
                    ignoreElements: (element) => {
                        // Ignore loading screen by ID
                        if (element.id === "uxbert-screenshot-loading") {
                            return true;
                        }
                        // Ignore Uxbert elements
                        if (element.getAttribute("data-uxbert-aminah") !== null ||
                            element.className?.toString().includes("uxbert-") ||
                            element.id?.includes("uxbert-")) {
                            return true;
                        }
                        // Skip cross-origin images that might cause issues
                        if (element.tagName === "IMG") {
                            const img = element;
                            try {
                                // Test if image is accessible
                                const testCanvas = document.createElement("canvas");
                                const ctx = testCanvas.getContext("2d");
                                testCanvas.width = 1;
                                testCanvas.height = 1;
                                ctx?.drawImage(img, 0, 0, 1, 1);
                                testCanvas.toDataURL();
                                return false; // Include the image
                            }
                            catch (e) {
                                console.warn("Skipping cross-origin image:", img.src);
                                return true;
                            }
                        }
                        return false;
                    },
                });
            }
            // Show UXbert UI elements again
            this.showUXbertElements();
            // Add canvas to context for afterCapture hook
            context.canvas = canvas;
            // Convert to base64 with configurable format and quality
            let screenshotData;
            if (this.format === "png") {
                screenshotData = canvas.toDataURL("image/png");
            }
            else if (this.format === "jpeg") {
                screenshotData = canvas.toDataURL("image/jpeg", this.quality);
            }
            else {
                screenshotData = canvas.toDataURL("image/webp", this.quality);
            }
            context.screenshot = screenshotData;
            // Hook: afterCapture - allow developers to modify the screenshot or canvas
            if (this.hooks.afterCapture) {
                const result = await Promise.resolve(this.hooks.afterCapture(context));
                // If hook returns a string, use it as the screenshot; otherwise use the original
                if (typeof result === "string" && result.length > 0) {
                    screenshotData = result;
                }
            }
            this.currentScreenshot = screenshotData;
            // Hook: onCaptureComplete - notify that capture completed successfully
            if (this.hooks.onCaptureComplete) {
                await Promise.resolve(this.hooks.onCaptureComplete(context));
            }
            return this.currentScreenshot;
        }
        catch (error) {
            console.error("Screenshot capture failed:", error);
            this.hideLoadingScreen();
            this.showUXbertElements();
            // Hook: onCaptureError - notify about the error
            if (this.hooks.onCaptureError) {
                await Promise.resolve(this.hooks.onCaptureError(error, context));
            }
            throw error;
        }
    }
    hideUXbertElements() {
        const elements = document.querySelectorAll('[class*="uxbert-"]');
        elements.forEach((el) => {
            const element = el;
            element.setAttribute("data-uxbert-display", element.style.display || "");
            element.style.display = "none";
        });
    }
    showUXbertElements() {
        const elements = document.querySelectorAll('[class*="uxbert-"]');
        elements.forEach((el) => {
            const element = el;
            const originalDisplay = element.getAttribute("data-uxbert-display");
            if (originalDisplay !== null) {
                element.style.display = originalDisplay;
                element.removeAttribute("data-uxbert-display");
            }
        });
    }
    showLoadingScreen() {
        // Check if loading screen already exists
        if (document.getElementById("uxbert-screenshot-loading")) {
            return;
        }
        const loadingOverlay = document.createElement("div");
        loadingOverlay.id = "uxbert-screenshot-loading";
        loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 2147483645;
      backdrop-filter: blur(4px);
    `;
        const spinner = document.createElement("div");
        spinner.style.cssText = `
      width: 50px;
      height: 50px;
      border: 4px solid rgba(255, 255, 255, 0.3);
      border-top: 4px solid #ffffff;
      border-radius: 50%;
      animation: uxbert-spin 1s linear infinite;
    `;
        const loadingText = document.createElement("div");
        loadingText.textContent = "Capturing screenshot...";
        loadingText.style.cssText = `
      color: #ffffff;
      font-size: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin-top: 20px;
      font-weight: 500;
    `;
        // Add keyframe animation if it doesn't exist
        if (!document.getElementById("uxbert-loading-styles")) {
            const style = document.createElement("style");
            style.id = "uxbert-loading-styles";
            style.textContent = `
        @keyframes uxbert-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
            document.head.appendChild(style);
        }
        loadingOverlay.appendChild(spinner);
        loadingOverlay.appendChild(loadingText);
        document.body.appendChild(loadingOverlay);
    }
    hideLoadingScreen() {
        const loadingOverlay = document.getElementById("uxbert-screenshot-loading");
        if (loadingOverlay) {
            loadingOverlay.remove();
        }
    }
    /**
     * Set the delay before capture in milliseconds
     * @param ms - Delay in milliseconds (default: 500)
     */
    setCaptureDelay(ms) {
        this.captureDelay = Math.max(0, Math.min(ms, 5000)); // Clamp between 0-5000ms
    }
    /**
     * Get the current capture delay
     */
    getCaptureDelay() {
        return this.captureDelay;
    }
    /**
     * Update screenshot hooks dynamically
     * @param hooks - New hooks configuration
     */
    setHooks(hooks) {
        this.hooks = { ...this.hooks, ...hooks };
        console.log("[Screenshot] Hooks updated:", {
            hasBeforeCapture: !!this.hooks.beforeCapture,
            hasAfterCapture: !!this.hooks.afterCapture,
            hasOnCaptureStart: !!this.hooks.onCaptureStart,
            hasOnCaptureComplete: !!this.hooks.onCaptureComplete,
            hasOnCaptureError: !!this.hooks.onCaptureError,
        });
    }
    /**
     * Get current hooks configuration
     */
    getHooks() {
        return { ...this.hooks };
    }
    /**
     * Set screenshot format
     * @param format - Image format (png, jpeg, webp)
     */
    setFormat(format) {
        this.format = format;
    }
    /**
     * Set screenshot quality (for JPEG/WebP)
     * @param quality - Quality between 0 and 1
     */
    setQuality(quality) {
        this.quality = Math.max(0, Math.min(quality, 1));
    }
    getScreenshot() {
        return this.currentScreenshot;
    }
    clear() {
        this.currentScreenshot = null;
    }
}
export default Screenshot;
//# sourceMappingURL=screenshot.js.map